import React from 'react';
import { Calendar, Droplet as DropletHalf, Wrench, Droplets, Building, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import HeroSection from '../components/HeroSection';
import FeatureSection from '../components/FeatureSection';
import ServiceCard from '../components/ServiceCard';
import TestimonialCard from '../components/TestimonialCard';
import CallToAction from '../components/CallToAction';

const HomePage: React.FC = () => {
  const services = [
    {
      id: 'weekly',
      title: 'Weekly Cleaning',
      description: 'Regular maintenance to keep your pool pristine all year round. Includes water testing, chemical balancing, skimming, and vacuuming.',
      icon: <Calendar className="h-6 w-6" />,
      link: '/quote?service=weekly'
    },
    {
      id: 'biweekly',
      title: 'Bi-Weekly Cleaning',
      description: "Perfect for pools with less frequent use. We'll ensure your pool stays clean and balanced with our bi-weekly service.",
      icon: <DropletHalf className="h-6 w-6" />,
      link: '/quote?service=biweekly'
    },
    {
      id: 'repairs',
      title: 'Pool Repairs',
      description: 'Expert repair services for pumps, filters, heaters and other pool equipment. Fast and reliable service when you need it most.',
      icon: <Wrench className="h-6 w-6" />,
      link: '/quote?service=repairs'
    },
    {
      id: 'green',
      title: 'Green to Clean',
      description: 'Is your pool green or neglected? Our specialized service will restore your pool to a crystal clear condition in no time.',
      icon: <Droplets className="h-6 w-6" />,
      link: '/quote?service=green'
    },
    {
      id: 'rental',
      title: 'Short Term Rental Care',
      description: 'Special care for vacation rental properties. We ensure your pool is always guest-ready with our reliable service.',
      icon: <Building className="h-6 w-6" />,
      link: '/quote?service=rental'
    }
  ];

  const testimonials = [
    {
      name: 'Jennifer K.',
      location: 'Orlando, FL',
      testimonial: "Splash Right has been maintaining our pool for over a year now and we couldn't be happier. Professional, on time, and our pool has never looked better!",
      rating: 5,
      date: 'March 2025'
    },
    {
      name: 'Michael T.',
      location: 'Winter Park, FL',
      testimonial: "After trying several other pool companies, we found Splash Right and they've been fantastic. Their attention to detail and customer service is outstanding.",
      rating: 5,
      date: 'February 2025'
    },
    {
      name: 'Sarah L.',
      location: 'Kissimmee, FL',
      testimonial: "We use their short-term rental service for our Airbnb property. They're reliable and our guests always comment on how clean the pool is!",
      rating: 4,
      date: 'January 2025'
    }
  ];

  return (
    <>
      <HeroSection />
      
      <FeatureSection />
      
      {/* Services Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-purple-900 mb-4">Our Pool Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive pool care solutions for residential and rental properties in Central Florida.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.slice(0, 3).map((service) => (
              <ServiceCard 
                key={service.id}
                id={service.id}
                title={service.title}
                description={service.description}
                icon={service.icon}
                link={service.link}
              />
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/services" className="inline-flex items-center text-orange-500 font-semibold hover:text-orange-700 transition-colors duration-200">
              View all services <ArrowRight className="ml-1 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>
      
      {/* Testimonials Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-purple-900 mb-4">What Our Customers Say</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Don't just take our word for it — here's what our satisfied customers have to say.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <TestimonialCard 
                key={index}
                name={testimonial.name}
                location={testimonial.location}
                testimonial={testimonial.testimonial}
                rating={testimonial.rating}
                date={testimonial.date}
              />
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/testimonials" className="inline-flex items-center text-orange-500 font-semibold hover:text-orange-700 transition-colors duration-200">
              Read more testimonials <ArrowRight className="ml-1 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>
      
      <CallToAction />
    </>
  );
};

export default HomePage;